import { DateTime } from '@ksyos/date-time';
import {
    Accordion,
    AccordionContent,
    AccordionContentIndent,
    AccordionItem,
    AccordionTrigger,
    Box,
    Heading,
} from '@ksyos/design-system';
import { isEmpty } from 'lodash';
import { useEffect, useState } from 'react';
import { MonthAccordion } from '@frontend/components/pages/requests/requestsList/RequestsListMonthAccordion';
import { getSelectedLanguage } from '@frontend/translate';
import type { ISearchFilter } from './RequestsListHeader';
import { useAbortableFetch } from '../../../../utils/hooks/useAbortableFetch';
import { buildGroupedWorklistUrl } from '../util/buildGroupedWorklistUrl';
import { requestFindFilter } from '../util/requestFindFilter';

export type RequestListAccordionProps = {
    children: JSX.Element;
    month: number | null;
    searchFilter: ISearchFilter;
    setMonth: (month: number | null) => Promise<void>;
    setMonthlyTotal: (total: number) => Promise<void>;
    setYear: (year: number | null) => Promise<void>;
    year: number | null;
};

/**
 * Grouped list of requests by year and month.
 */
export type GroupedList = { [key: string]: { [key: string]: number } };

export function RequestListAccordion(props: RequestListAccordionProps) {
    const translate = getSelectedLanguage();
    const abortableFetch = useAbortableFetch();

    const [groupedList, setGroupedList] = useState<GroupedList>({});
    const [isLoading, setIsLoading] = useState(false);

    const { children, month: monthProp, searchFilter, setMonth, setMonthlyTotal, setYear, year: yearProp } = props;

    useEffect(() => {
        loadGroupedList();
    }, [searchFilter]);

    useEffect(() => {
        updateMonthlyTotal(monthProp, yearProp, groupedList);
    }, [monthProp, yearProp, groupedList]);

    useEffect(() => {
        if (yearProp === null) {
            const now = DateTime.now();
            setYear(now.year());
            setMonth(now.month());
        } else if (monthProp === null) {
            setMonth(DateTime.now().month());
        }
    }, [yearProp, monthProp]);

    // Show empty state if groupedList is empty and not loading
    if (isEmpty(groupedList) && !isLoading) {
        return <Heading size="xlarge">{translate.request.emptyClosedRequestsWorklist}</Heading>;
    }

    const years = Object.keys(groupedList).reverse();

    async function handleYearSelection(yearToUpdate: number) {
        // check if the year is already selected
        if (yearToUpdate !== yearProp) {
            setYear(yearToUpdate);
        }
    }

    return (
        <Accordion
            collapsible={true}
            defaultValue={yearProp ? String(yearProp) : undefined}
            type="single"
        >
            {years.map((year) => {
                const yearAsNumber = Number(year);
                const totalRequestsThisYear = Object.values(groupedList[year]).reduce((acc, value) => acc + value, 0);

                return (
                    <AccordionItem
                        key={year}
                        onClick={() => handleYearSelection(yearAsNumber)}
                        value={year}
                    >
                        <AccordionTrigger icon="calender">
                            {year}
                            <Box
                                color="neutral400"
                                ml={2}
                            >
                                ({totalRequestsThisYear})
                            </Box>
                        </AccordionTrigger>
                        <AccordionContent>
                            <AccordionContentIndent>
                                <MonthAccordion
                                    groupedList={groupedList}
                                    month={monthProp}
                                    setMonth={setMonth}
                                    year={yearAsNumber}
                                >
                                    {children}
                                </MonthAccordion>
                            </AccordionContentIndent>
                        </AccordionContent>
                    </AccordionItem>
                );
            })}
        </Accordion>
    );

    // Fetch grouped list data and handle state updates
    async function loadGroupedList() {
        setIsLoading(true);
        try {
            const filter = requestFindFilter(searchFilter.text);
            const url = buildGroupedWorklistUrl(searchFilter, filter);
            const list = await abortableFetch.getJson(url);

            setGroupedList(list.json);
        } finally {
            setIsLoading(false);
        }
    }

    // Updates the monthly total based on the provided month, year, and list.
    async function updateMonthlyTotal(monthToQuery: number | null, yearToQuery: number | null, list: GroupedList) {
        if (monthToQuery !== null && yearToQuery) {
            await setMonthlyTotal(list[yearToQuery]?.[monthToQuery]);
        }
    }
}
